<template>
  <div
    class="letter-selection min-h-[100dvh] max-h-[100dvh] overflow-hidden flex flex-col bg-transparent relative"
  >
    <!-- FitToHeight scales the board to the viewport so both tile rows, the
         timer, status, and controls stay visible without scrolling (issue #289).
         The connection-status aside is a sibling OUTSIDE this wrapper: it is
         position:fixed and a transformed ancestor would re-base it. -->
    <FitToHeight class="flex-1 min-h-0">
      <main
        class="letter-selection-main flex flex-col items-center gap-3 py-3 px-4 max-w-[1200px] mx-auto w-full box-border md:gap-8 md:py-8 md:px-6 lg:gap-8 lg:p-8"
        role="main"
        aria-labelledby="letter-selection-main-heading"
      >
        <!-- Page heading kept for screen readers (no visible hero banner, to stay
           consistent with the other in-game screens). -->
        <h1 id="letter-selection-main-heading" class="sr-only">
          {{ STRINGS.ui.titles.letterSelectionGame }}
        </h1>

        <section
          role="region"
          aria-labelledby="letter-grid-heading"
          class="w-full flex justify-center items-center shrink-0"
        >
          <h4 id="letter-grid-heading" class="sr-only">{{ STRINGS.ui.titles.selectedLetters }}</h4>
          <!-- Shared arena-card frame so the tiles sit in the same surface as the
             word-finding screen (consistent framing across the two phases). -->
          <div class="rd-card w-full max-w-[500px] mx-auto">
            <LetterGrid
              :letters="visibleLetters as Letter[]"
              :current-slot="visibleLetters.length"
              :animation-state="animationState"
            />
          </div>
        </section>

        <section
          v-if="timerIsActive || timerTimeRemaining > 0"
          role="region"
          aria-labelledby="selection-timer-heading"
          class="w-full flex justify-center items-center"
        >
          <h4 id="selection-timer-heading" class="sr-only">Selection Timer</h4>
          <SelectionTimer
            :time-remaining="timerTimeRemaining"
            :total-time="timerTotalTime"
            :is-active="timerIsActive"
          />
        </section>

        <section
          role="region"
          aria-labelledby="game-status-heading"
          class="w-full flex justify-center items-center"
        >
          <h4 id="game-status-heading" class="sr-only">{{ STRINGS.ui.titles.gameStatus }}</h4>
          <PlayerStatus
            :players="players"
            :current-selector="letterSelection.currentSelector.value"
            :current-player-id="currentPlayerId"
            :status="letterSelection.selectionStatus.value"
            :vowel-count="letterSelection.vowelCount.value"
            :consonant-count="letterSelection.consonantCount.value"
          />
        </section>

        <section
          role="region"
          aria-labelledby="controls-heading"
          class="w-full flex justify-center items-center"
        >
          <h4 id="controls-heading" class="sr-only">
            {{ STRINGS.ui.titles.letterSelectionControls }}
          </h4>
          <SelectionControls
            :vowel-enabled="letterSelection.vowelButtonEnabled.value"
            :consonant-enabled="letterSelection.consonantButtonEnabled.value"
            :vowel-count="letterSelection.vowelCount.value"
            :consonant-count="letterSelection.consonantCount.value"
            :is-player-turn="letterSelection.isPlayerTurn.value"
            :status="letterSelection.selectionStatus.value"
            @select-letter="handleLetterSelection"
            @auto-select="handleAutoSelect"
          />
        </section>
      </main>
    </FitToHeight>

    <aside
      v-if="wsConnectionStatus !== 'connected'"
      class="connection-status fixed bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 py-3 px-6 rounded-3xl text-sm font-semibold z-[1000] shadow-lg"
      :class="{
        'bg-energy-warning text-white': wsConnectionStatus === 'connecting',
        'bg-gray-500 text-white': wsConnectionStatus === 'disconnected',
        'bg-error-500 text-white': wsConnectionStatus === 'error',
      }"
      role="alert"
      aria-live="assertive"
      aria-labelledby="connection-status-heading"
    >
      <h4 id="connection-status-heading" class="sr-only">
        {{ STRINGS.ui.titles.connectionStatus }}
      </h4>
      <span
        class="connection-message whitespace-nowrap"
        aria-describedby="connection-status-heading"
      >
        {{ connectionStatusMessage }}
      </span>
      <div
        v-if="wsConnectionStatus === 'connecting'"
        class="loading-spinner"
        :aria-label="STRINGS.ui.loading.connecting"
      ></div>
      <nav
        v-if="manualReconnectAvailable"
        role="navigation"
        :aria-label="STRINGS.aria.labels.connectionRecovery"
      >
        <button
          @click="handleManualReconnect"
          class="manual-reconnect-button bg-white/20 border border-white/30 text-white py-2 px-4 rounded-xl text-sm font-semibold cursor-pointer transition-all duration-200 ease-out shrink-0 hover:bg-white/30 hover:border-white/50 hover:-translate-y-px active:translate-y-0"
          :aria-label="STRINGS.aria.labels.retryConnectionManually"
        >
          {{ STRINGS.ui.buttons.tryAgain }}
        </button>
      </nav>
    </aside>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import LetterGrid from './LetterGrid.vue';
import SelectionControls from './SelectionControls.vue';
import SelectionTimer from './SelectionTimer.vue';
import PlayerStatus from './PlayerStatus.vue';
import FitToHeight from './FitToHeight.vue';
import { useWebSocket } from '@/composables/useWebSocket';
import type { ConnectionStatus } from '@/composables/useWebSocket';
import { useLetterSelection } from '@/composables/useLetterSelection';
import { useLetterRevealQueue } from '@/composables/useLetterRevealQueue';
import { STRINGS } from '@/constants/strings';
import type { Player, Letter, LetterKind, SelectionStatus, ClientMessage } from '@/types/generated';

interface Props {
  gameId: string;
  players: Player[];
  currentPlayerId: string;
  selectedLetters: Letter[];
  selectorPlayerId: string | null;
  selectionEnabled: boolean | null;
  vowelButtonEnabled: boolean | null;
  consonantButtonEnabled: boolean | null;
  selectionStatus: SelectionStatus | null;
  timerTimeRemaining: number;
  timerTotalTime: number;
  timerIsActive: boolean;
  wsConnectionStatus?: ConnectionStatus;
  manualReconnectAvailable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  wsConnectionStatus: 'connected',
  manualReconnectAvailable: false,
});

const emit = defineEmits<{
  error: [message: string];
  connectionLost: [];
}>();

// WebSocket composable
const { sendMessage, isConnected, reconnectAttempt, maxReconnectAttempts, manualReconnect } =
  useWebSocket();

// Letter selection business logic composable
const letterSelection = useLetterSelection({
  gameId: props.gameId,
  players: props.players,
  currentPlayerId: props.currentPlayerId,
});

// Letter reveal queue - buffers rapid arrivals and staggers animation
const allLettersRef = computed(() => props.selectedLetters);
const { visibleLetters, isRevealing } = useLetterRevealQueue(allLettersRef, {
  onQueueDrained: () => {
    // All 10 letters revealed - signal server we're ready
    if (!isConnected.value) return;
    const message: ClientMessage = {
      ReadyForFinding: { game_id: props.gameId },
    };
    sendMessage(message);
  },
});

// Map isRevealing to the existing animation state contract
const animationState = computed<'idle' | 'revealing' | 'complete'>(() => {
  if (visibleLetters.value.length >= 10) return 'complete';
  if (isRevealing.value) return 'revealing';
  return 'idle';
});

// UI-focused computed properties
const connectionStatusMessage = computed(() => {
  switch (props.wsConnectionStatus) {
    case 'connecting':
      if (reconnectAttempt.value > 0) {
        return STRINGS.templates.reconnectAttempt(
          reconnectAttempt.value,
          maxReconnectAttempts.value
        );
      }
      return STRINGS.status.connection.connecting;
    case 'error':
      if (props.manualReconnectAvailable) {
        return STRINGS.status.connection.connectionFailed;
      }
      return STRINGS.status.connection.connectionError;
    case 'disconnected':
      return STRINGS.status.connection.disconnected;
    default:
      return '';
  }
});

// Watch selection props from App.vue (driven by GameSnapshot handler)
watch(
  () =>
    [
      props.selectedLetters,
      props.selectorPlayerId,
      props.selectionEnabled,
      props.vowelButtonEnabled,
      props.consonantButtonEnabled,
      props.selectionStatus,
    ] as const,
  ([letters, selector, enabled, vowelEnabled, consonantEnabled, status]) => {
    letterSelection.setSelectionState({
      selectedLetters: letters,
      selectionEnabled: enabled,
      vowelButtonEnabled: vowelEnabled,
      consonantButtonEnabled: consonantEnabled,
      selectionStatus: status,
      selectorPlayerId: selector,
    });
  },
  { immediate: true }
);

// Handle letter selection from controls with validation
const handleLetterSelection = (letterType: LetterKind) => {
  if (!isConnected.value) {
    emit('error', STRINGS.errors.component.noWebSocket);
    return;
  }

  // Validate selection using business logic
  const validation = letterSelection.validateLetterSelection(letterType);
  if (!validation.isValid) {
    if (validation.reason !== 'Not your turn') {
      emit('error', validation.reason || 'Invalid selection');
    }
    return;
  }

  // Send selection to server with idempotency token
  const message: ClientMessage = {
    SelectLetter: {
      game_id: props.gameId,
      letter_type: letterType,
      idempotency_token: crypto.randomUUID(),
    },
  };

  const sent = sendMessage(message);
  if (!sent) {
    emit('error', STRINGS.errors.connection.failedToSubmit);
  }
};

// Handle auto-select ("pick for me"): ask the server to deal all remaining
// letters at once. The server validates the selector and enforces the
// vowel/consonant caps, so no client-side letter validation is needed — the
// existing snapshot → reveal-queue path renders the staggered reveal.
const handleAutoSelect = () => {
  if (!isConnected.value) {
    emit('error', STRINGS.errors.component.noWebSocket);
    return;
  }

  const message: ClientMessage = {
    AutoSelectLetters: {
      game_id: props.gameId,
    },
  };

  const sent = sendMessage(message);
  if (!sent) {
    emit('error', STRINGS.errors.connection.failedToSubmit);
  }
};

// Handle manual reconnect
const handleManualReconnect = async () => {
  try {
    await manualReconnect();
  } catch (error) {
    emit('error', STRINGS.errors.component.manualReconnectFailed);
  }
};

// Watch for connection state changes
watch(
  () => props.wsConnectionStatus,
  newStatus => {
    if (newStatus === 'error' || newStatus === 'disconnected') {
      emit('connectionLost');
    }
  }
);

onMounted(() => {
  if (!isConnected.value) {
    console.warn('[LetterSelection] WebSocket not connected, component may not function properly');
    emit('error', STRINGS.errors.component.noWebSocket);
  }
});
</script>

<style scoped>
.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  flex-shrink: 0;
}

@media (prefers-contrast: high) {
  .connection-status {
    border: 2px solid white;
  }
}

@media (prefers-reduced-motion: reduce) {
  .loading-spinner {
    animation: none;
  }
}
</style>
